import AvatarIcon, { avatarIcon } from 'components/features/utils/AvatarIcon';
import {
  CommaFormattedPercentageString,
  PercentAndDeltaVerboseFormattedString,
} from '../utils/formatter';
import React from 'react';

type AvatarIconType = avatarIcon;
export type CalcAvatarIconType = (totalAssetRate: number) => AvatarIconType;
export type AdvisorCommentProps =
  | NoAccountLinkageAdvisorCommentProps
  | AccountLinkageAdvisorCommentProps;
type NoAccountLinkageAdvisorCommentProps = {
  userName: string;
};

type SuggestionCommentProps = {
  userName: string;
};

type AccountLinkageAdvisorCommentProps = {
  month: number;
  changeTotalAssetRate: CommaFormattedPercentageString;
  userName: string;
  comparisonData: ComparisonData;
  avatarIconType: AvatarIconType;
  getCommentTitle: GetCommentTitle;
  getCommentContent: GetCommentContent;
};

export type GetCommentTitle = ((val: number) => string) | (() => string);
export type GetCommentContent = (
  avatarIconType: AvatarIconType,
  changeTotalAssetRate: CommaFormattedPercentageString,
  userName: string
) => string;
type SupportCommentProps = {
  month: number;
  avatorIconType: AvatarIconType;
  changeTotalAssetRate: CommaFormattedPercentageString;
  userName: string;
};

type ComparisonData = [
  {
    name: '国内株式';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '未上場株式';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '投資信託';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '外国株式';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '国内ETF・ETN';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '外国ETF・ETN';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '国内REIT';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '外国REIT';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '国内債券';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '外国債券';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '国内EB';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '外国EB';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '不動産';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: 'ロボアド';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '信用取引';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: '預金・MRF';
    change: PercentAndDeltaVerboseFormattedString;
  },
  {
    name: 'その他';
    change: PercentAndDeltaVerboseFormattedString;
  }
];
type ConstructComparisonReport = (data: ComparisonData) => JSX.Element;
type ComparisonCommentProps = {
  data: ComparisonData;
};

const getSupportComment = (
  getCommentContent: GetCommentContent,
  getCommentTitle: GetCommentTitle
): React.FC<SupportCommentProps> => ({
  month,
  avatorIconType,
  changeTotalAssetRate,
  userName,
}): ReturnType<React.FC<SupportCommentProps>> => {
  // 後々週次レポート等で使えるように関数は依存性注入
  const commentTitle = getCommentTitle(month);
  const commentContent = getCommentContent(
    avatorIconType,
    changeTotalAssetRate,
    userName
  );

  return (
    <>
      <li className="p-3.5 text-sm bg-white border border-gray-300 rounded-7">
        <div className="font-bold">{commentTitle}</div>
        <div className="mt-2 text-sm" style={{ lineHeight: '1.8' }}>
          {commentContent}
        </div>
      </li>
    </>
  );
};

const ComparisonComment: React.FC<ComparisonCommentProps> = ({ data }) => {
  const constructComparisonReport: ConstructComparisonReport = data => {
    return (
      <>
        運用中の資産は
        {data.map(({ name, change }, index) => {
          if (change === null) return <></>;
          const isFirstItem = index === 0;
          return (
            <React.Fragment key={index}>
              <br />「{name}」{isFirstItem ? 'が' : 'は'}
              {change}
            </React.Fragment>
          );
        })}
      </>
    );
  };
  const comparisonReport = constructComparisonReport(data);

  return (
    <>
      <li className="mt-2.5 p-3.5 text-sm bg-white border border-gray-300 rounded-7">
        <div className="text-sm" style={{ lineHeight: '1.8' }}>
          {comparisonReport}
        </div>
      </li>
    </>
  );
};

const AccountLinkageAdvisorComment: React.FC<AccountLinkageAdvisorCommentProps> = ({
  userName,
  avatarIconType,
  month,
  changeTotalAssetRate,
  comparisonData,
  getCommentContent,
  getCommentTitle,
}) => {
  const SupportComment = getSupportComment(getCommentContent, getCommentTitle);

  return (
    <>
      <div className="mt-8">
        <div className="flex items-start">
          <AvatarIcon icon={avatarIconType} />
          <ul className="flex-1 pl-2.5">
            <SupportComment
              avatorIconType={avatarIconType}
              userName={userName}
              changeTotalAssetRate={changeTotalAssetRate}
              month={month}
            />
            <ComparisonComment data={comparisonData} />
          </ul>
        </div>
      </div>
    </>
  );
};

const SuggestionComment: React.FC<SuggestionCommentProps> = ({ userName }) => {
  return (
    <>
      <li className="p-3.5 text-sm bg-white border border-gray-300 rounded-7">
        <div className="font-bold">口座・資産情報を連携しませんか？</div>
        <div className="mt-2 text-sm" style={{ lineHeight: '1.8' }}>
          現在資産情報の登録が無いようです。口座を連携すると
          {userName}さんの資産運用の成果を見える化することが出来ます。
        </div>
      </li>
    </>
  );
};

const NoAccountLinkageAdvisorComment: React.FC<NoAccountLinkageAdvisorCommentProps> = ({
  userName,
}) => {
  const avatarIconType: AvatarIconType = 'good';
  return (
    <>
      <div className="mt-8 p-0 bg-transparent">
        <div className="flex items-start">
          <AvatarIcon icon={avatarIconType} />
          <ul className="flex-1 pl-2.5">
            <SuggestionComment userName={userName} />
          </ul>
        </div>
      </div>
    </>
  );
};

export const AdvisorComment: React.FC<AdvisorCommentProps> = props => {
  if ('changeTotalAssetRate' in props) {
    return <AccountLinkageAdvisorComment {...props} />;
  } else {
    return <NoAccountLinkageAdvisorComment {...props} />;
  }
};
